import { useParams, Link, Navigate } from "react-router-dom";
import Seo, { articleSchema, breadcrumbSchema } from "@/components/Seo";
import Crumbs from "@/components/Crumbs";
import HelpCta from "@/components/HelpCta";
import { BLOG_POSTS, getPost } from "@/data/blog";
import { Clock, ArrowLeft, ArrowUpRight, Info, AlertTriangle, Heart } from "lucide-react";

function formatDate(iso) {
  return new Date(iso).toLocaleDateString("es-CL", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function renderBlock(block, i) {
  if (block.type === "p") return <p key={i}>{block.text}</p>;
  if (block.type === "h2") return <h2 key={i}>{block.text}</h2>;
  if (block.type === "h3") return <h3 key={i}>{block.text}</h3>;
  if (block.type === "quote")
    return <blockquote key={i}>"{block.text}"</blockquote>;
  if (block.type === "ul")
    return (
      <ul key={i}>
        {block.items.map((it, j) => (
          <li key={j}>{it}</li>
        ))}
      </ul>
    );
  if (block.type === "ol")
    return (
      <ol key={i}>
        {block.items.map((it, j) => (
          <li key={j}>{it}</li>
        ))}
      </ol>
    );
  if (block.type === "callout") {
    const tone = block.tone || "emerald";
    const color = {
      emerald: { ring: "border-emerald-500/30 bg-emerald-500/5", text: "text-emerald-300", Icon: Heart },
      red: { ring: "border-red-500/30 bg-red-500/5", text: "text-red-300", Icon: AlertTriangle },
      cyan: { ring: "border-cyan-500/30 bg-cyan-500/5", text: "text-cyan-300", Icon: Info },
    }[tone];
    const Icon = color.Icon;
    return (
      <aside
        key={i}
        className={`not-prose my-6 flex gap-4 rounded-2xl border p-5 ${color.ring}`}
      >
        <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${color.text}`} />
        <div>
          <div className={`text-sm font-semibold ${color.text}`}>{block.title}</div>
          <p className="mt-1 text-sm leading-relaxed text-slate-300">{block.text}</p>
        </div>
      </aside>
    );
  }
  return null;
}

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPost(slug);

  if (!post) return <Navigate to="/blog" replace />;

  const crumbs = [
    { name: "Blog", path: "/blog" },
    { name: post.title, path: `/blog/${post.slug}` },
  ];
  const schemas = [
    articleSchema({
      title: post.title,
      description: post.description,
      path: `/blog/${post.slug}`,
      datePublished: post.datePublished,
    }),
    breadcrumbSchema([{ name: "Inicio", path: "/" }, ...crumbs]),
  ];

  const related = BLOG_POSTS.filter((p) => p.slug !== post.slug).slice(0, 3);

  return (
    <>
      <Seo
        title={`${post.title} | pasta-base.cl`}
        description={post.description}
        path={`/blog/${post.slug}`}
        type="article"
        schema={schemas}
      />

      <article className="mx-auto max-w-3xl px-5 py-16 lg:px-8">
        <Crumbs items={crumbs} />

        <header>
          <Link
            to="/blog"
            className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-emerald-400 hover:text-emerald-300"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Volver al blog
          </Link>
          <h1 className="mt-5 text-4xl font-extrabold tracking-tight text-slate-50 sm:text-5xl">
            {post.title}
          </h1>
          <div className="mt-5 flex flex-wrap items-center gap-4 text-sm text-slate-400">
            <span>{formatDate(post.datePublished)}</span>
            <span className="inline-flex items-center gap-1">
              <Clock className="h-4 w-4" />
              {post.readingTime}
            </span>
            {post.keywords?.slice(0, 2).map((k) => (
              <span
                key={k}
                className="rounded-full border border-slate-800 bg-slate-900/50 px-2.5 py-0.5 text-xs text-slate-400"
              >
                {k}
              </span>
            ))}
          </div>
          <p className="mt-6 text-lg leading-relaxed text-slate-300">{post.excerpt}</p>
        </header>

        <div className="prose-dark mt-10" data-testid="post-content">
          {post.content.map(renderBlock)}
        </div>

        {/* Mini CTA inside article */}
        <div className="mt-14 rounded-3xl border border-emerald-500/20 bg-gradient-to-br from-emerald-500/5 to-slate-900 p-8">
          <p className="text-base text-slate-200">
            ¿Tú o alguien cercano necesita ayuda? No estás solo. La red
            SinAdicciones.org agrupa centros y profesionales en toda Chile.
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <a
              href="https://sinadicciones.org/centros-de-rehabilitacion-drogas-chile"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-press inline-flex items-center gap-2 rounded-full bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-slate-950 hover:bg-emerald-400"
            >
              Buscar Centros
              <ArrowUpRight className="h-4 w-4" />
            </a>
            <Link
              to="/test-dependencia"
              className="inline-flex items-center gap-2 rounded-full border border-slate-700 bg-slate-900 px-5 py-2.5 text-sm font-semibold text-slate-100 hover:bg-slate-800"
            >
              Hacer Test de Dependencia
            </Link>
          </div>
        </div>
      </article>

      {/* Related */}
      <section className="border-t border-slate-800 bg-slate-950">
        <div className="mx-auto max-w-6xl px-5 py-16 lg:px-8">
          <h2 className="text-2xl font-bold tracking-tight text-slate-50">
            Sigue leyendo
          </h2>
          <div className="mt-6 grid gap-4 md:grid-cols-3">
            {related.map((p) => (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}`}
                className="group rounded-2xl border border-slate-800 bg-slate-900/50 p-5 transition hover:border-emerald-500/40"
              >
                <div className="text-xs text-slate-400">{formatDate(p.datePublished)}</div>
                <h3 className="mt-2 text-base font-semibold leading-tight text-slate-50 group-hover:text-emerald-300">
                  {p.title}
                </h3>
                <p className="mt-2 line-clamp-3 text-sm text-slate-400">{p.excerpt}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-slate-800 bg-slate-950">
        <div className="mx-auto max-w-7xl px-5 py-16 lg:px-8">
          <HelpCta testidPrefix="blog-post" />
        </div>
      </section>
    </>
  );
}
